import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    confirmRompslompSupplier, getRompslompSuppliers, linkRompslompSupplier, sendRompslompBatch, sendRompslompSupplier,
} from '../../../services/RompslompService';
import { SendConfirmModal } from './RompslompSending';
import { apiMessage } from './rompslompUtils';
import { decodeEntities } from '../../../utils/rompslompLabels';

const STATUSES = ['ready', 'needs_confirmation', 'missing_fields', 'cache_match', 'needs_attention', 'linked'];
const STATUS_CLASS = { ready: 'text-success', needs_attention: 'text-danger' };

export default function RompslompSuppliers({ sending, reloadSending }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [confirm, setConfirm] = useState(null);
    const [link, setLink] = useState(null);
    const [busy, setBusy] = useState(false);
    const [batchAsk, setBatchAsk] = useState(false);

    const load = useCallback(async () => {
        try { setData((await getRompslompSuppliers()).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const setConfirmed = async (row, confirmed) => {
        try { setData((await confirmRompslompSupplier(row.id, confirmed)).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };

    const create = async () => {
        setBusy(true);
        try {
            const res = await sendRompslompSupplier(confirm.id, confirm.payload_hash);
            toast[res.data.result.ok ? 'success' : 'warning'](t(`rompslomp.log.status.${res.data.result.status}`));
            setConfirm(null);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const batch = async () => {
        setBusy(true);
        setBatchAsk(false);
        try {
            const res = (await sendRompslompBatch('suppliers')).data.result;
            toast[res.stopped_because ? 'warning' : 'success'](t('rompslomp.suppliers.batchDone', { sent: res.sent, remaining: res.remaining }));
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const doLink = async (contactId) => {
        try { setData((await linkRompslompSupplier(link.id, contactId)).data); setLink(null); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };

    const rows = useMemo(() => (data ? data.rows.filter((r) => (filter === 'all' || r.status === filter)
        && (!search || `${r.name} ${r.legal_name} ${r.kvk_number} ${r.vat_number}`.toLowerCase().includes(search.toLowerCase()))) : []), [data, filter, search]);

    if (!data) return <Spinner animation="border" />;
    const by = data.summary.by_status;
    const canSend = sending.can_send;
    const unlocked = !!sending.supplier_batch_unlocked;
    const batchReady = Math.min(by.ready, sending.batch_max);

    return (
        <>
            <p className="text-muted">{t('rompslomp.suppliers.hint')}</p>
            <div className="d-flex flex-wrap gap-2 mb-3" data-testid="supplier-status-counts">
                {STATUSES.map((k) => <span key={k} className="me-3">{t(`rompslomp.suppliers.status.${k}`)}: <strong>{by[k]}</strong></span>)}
            </div>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                <Form.Select size="sm" className="rompslomp-filter" aria-label={t('rompslomp.suppliers.filter')} value={filter} onChange={(e) => setFilter(e.target.value)}>
                    <option value="all">{t('rompslomp.expenses.allStatuses')}</option>
                    {STATUSES.map((k) => <option key={k} value={k}>{t(`rompslomp.suppliers.status.${k}`)}</option>)}
                </Form.Select>
                <Form.Control size="sm" className="rompslomp-filter" placeholder={t('rompslomp.suppliers.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
                <Button size="sm" variant="danger" disabled={!canSend || !unlocked || busy || by.ready === 0} onClick={() => setBatchAsk(true)} data-testid="batch-suppliers"
                    title={unlocked ? '' : t('rompslomp.suppliers.batchLockedHint')}>
                    {unlocked ? '' : <i className="fa-solid fa-lock me-1" />}{t('rompslomp.suppliers.createAll', { max: sending.batch_max })}
                </Button>
                <span className="text-muted small">{t('rompslomp.suppliers.batchState', { state: unlocked ? t('rompslomp.sending.unlocked') : t('rompslomp.sending.locked') })}</span>
            </div>
            <Table className="rompslomp-table rompslomp-suppliers" size="sm" hover data-testid="supplier-table">
                <thead><tr><th className="c-main">{t('rompslomp.suppliers.supplier')}</th><th className="c-status">{t('rompslomp.preview.state')}</th><th>{t('rompslomp.suppliers.missing')}</th>
                    <th className="c-narrow">{t('rompslomp.suppliers.confirmed')}</th><th>{t('rompslomp.suppliers.contact')}</th><th className="c-actions" /></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`supplier-row-${r.id}`}>
                            <td className="c-main" data-label={t('rompslomp.suppliers.supplier')}><strong>{r.name}</strong>
                                {r.legal_name && r.legal_name !== r.name && <div className="small text-muted">{r.legal_name}</div>}
                                <div className="small text-muted">{[r.street, [r.postcode, r.city].filter(Boolean).join(' '), r.country].filter(Boolean).join(', ')}</div>
                                {r.kvk_number && <div className="small text-muted">KvK {r.kvk_number}{r.vat_number ? ` · ${r.vat_number}` : ''}</div>}
                            </td>
                            <td className="c-status" data-label={t('rompslomp.preview.state')}>
                                <span className={STATUS_CLASS[r.status] || ''}>{t(`rompslomp.suppliers.status.${r.status}`)}</span>
                                {r.push?.last_error && <div className="small text-danger">{r.push.last_error}</div>}
                                {r.matches.length > 0 && <div className="small text-muted">{t('rompslomp.suppliers.matches', { list: r.matches.map((m) => `${decodeEntities(m.name)} (${m.by})`).join(', ') })}</div>}
                            </td>
                            <td data-label={t('rompslomp.suppliers.missing')} className="small">{r.missing.length ? r.missing.map((f) => t(`suppliers.accounting.fields.${f}`)).join(', ') : '-'}</td>
                            <td data-label={t('rompslomp.suppliers.confirmed')}>
                                <Form.Check type="checkbox" id={`confirm-supplier-${r.id}`} aria-label={`${t('rompslomp.suppliers.confirmed')} ${r.name}`} checked={r.confirmed}
                                    disabled={!!r.rompslomp_contact_id} onChange={(e) => setConfirmed(r, e.target.checked)} />
                            </td>
                            <td data-label={t('rompslomp.suppliers.contact')}>{r.rompslomp_contact_id ? `${r.rompslomp_contact_id} (${t(`rompslomp.suppliers.contactStatus.${r.rompslomp_status}`)})` : t('rompslomp.suppliers.toCreate')}</td>
                            <td className="c-actions">
                                {r.status === 'cache_match' && <Button size="sm" variant="outline-info" onClick={() => setLink(r)} data-testid={`link-supplier-${r.id}`}>{t('rompslomp.suppliers.link')}</Button>}
                                <Button size="sm" variant="outline-danger" disabled={r.status !== 'ready' || !canSend || !r.payload} onClick={() => setConfirm(r)} data-testid={`create-supplier-${r.id}`}>
                                    {t('rompslomp.suppliers.create')}
                                </Button>
                            </td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan="6" className="text-center text-muted">{t('rompslomp.suppliers.empty')}</td></tr>}
                </tbody>
            </Table>
            <SendConfirmModal show={!!confirm} title={confirm ? `${t('rompslomp.suppliers.create')}: ${confirm.name}` : ''}
                payload={confirm?.payload} hash={confirm?.payload_hash} busy={busy} onConfirm={create} onHide={() => setConfirm(null)} />
            <Modal show={batchAsk} onHide={() => setBatchAsk(false)} centered>
                <Modal.Header closeButton><Modal.Title>{t('rompslomp.suppliers.createAll', { max: sending.batch_max })}</Modal.Title></Modal.Header>
                <Modal.Body>{t('rompslomp.suppliers.batchConfirm', { count: batchReady })}</Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setBatchAsk(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                    <Button variant="danger" onClick={batch} data-testid="confirm-batch-suppliers">{t('rompslomp.suppliers.create')}</Button>
                </Modal.Footer>
            </Modal>
            <Modal show={!!link} onHide={() => setLink(null)} centered>
                <Modal.Header closeButton><Modal.Title>{t('rompslomp.suppliers.link')}: {link?.name}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p>{t('rompslomp.suppliers.linkText')}</p>
                    {(link?.matches || []).map((m) => (
                        <div key={m.contact_id} className="d-flex justify-content-between align-items-center mb-2">
                            <span>{decodeEntities(m.name)} ({m.by}){!m.is_supplier ? ` - ${t('rompslomp.suppliers.notSupplier')}` : ''}</span>
                            <Button size="sm" disabled={!m.is_supplier} onClick={() => doLink(m.contact_id)}>{t('rompslomp.suppliers.useThis')}</Button>
                        </div>
                    ))}
                </Modal.Body>
            </Modal>
        </>
    );
}
